import User from "@/entity/User";
import Contract from "@/entity/Contract";
import Insurer from "@/entity/Insurer";
import PackCategory from "@/entity/PackCategory";
import Pack from "@/entity/Pack";
import PremiumDetailModel from "@/entity/PremiumDetailModel";
import CommissionModel from "@/entity/CommissionModel";
import ReductionModel from "@/entity/ReductionModel";
import Vue from 'vue'
import Popup from "@/entity/Popup";
import {checkItemPropertiesVisibilities} from "@/services/ItemFunction";

export interface ContractStore {
    branch?: any,
    guarantees: any,
    insurers: Insurer[],
    insurer: any,
    packCategories: PackCategory[],
    packs: Pack[],
    premiumDetailModels: PremiumDetailModel[],
    commissionModels: CommissionModel[],
    reductionModel: ReductionModel[],
    contract?: Contract,
    itemTypes: any,
    avenantType: any,
    propertyTypes: any,
    items: any,
    currentItem: any,
    properties: any,
}

const state: any = {
    branch: undefined,
    guarantees: {},
    insurers: {},
    insurer: null as any,
    assets: [],
    packCategories: {},
    packs: [],
    premiumDetailModels: {},
    commissionModels: {},
    reductionModels: {},
    contract: new Contract(),
    itemTypes: {},
    editedItem: null,
    avenantType: null,
    currentItem: null,
    propertyTypes: {},
    itemsExpanded: [],
    items: {},
    rootUuid: null,
    childrenSize: 3,
    requiredItems: {},
    subscriber: null,
    avenant: null,
    properties: {},
}

const mutations = {
    addEntity(state: any, {type, item, property = 'uuid'}: any) {
        if (!item.hasOwnProperty(property)) {
            new Popup("Erreur d'enregistrement", "un objet " + type + ' ne peut pas être enregistré car il ne possède pas de propriété ' + property)
            console.warn(type + ' sans ' + property + ' : impossible de l\'ajouter');
            return;
        }
        if (!state.hasOwnProperty(type)) {
            Vue.set(state, type, {});
        }
        Vue.set(state[type], item[property], item);
    },

    setEntity(state: any, {type, data}: any) {
        if (!state.hasOwnProperty(type)) {
            Vue.set(state, type, {});
        }
        Vue.set(state, type, data);
    },


    setItemTypes(state: any, itemTypes: any) {
        state.itemTypes = itemTypes
    },

    removeEntity(state: any, {target, uuid}: any) {
        if (state[target] && state[target].hasOwnProperty(uuid)) {
            Vue.delete(state[target], uuid);
        }
    },
    removeEntities(state: any, {target, uuids}: any) {
        uuids.forEach((uuid: any) => {
            if (state[target] && state[target].hasOwnProperty(uuid)) {
                Vue.delete(state[target], uuid);
            }
        })
    },
    setItemsExpanded(state: any, uuids: any) {
        state.itemsExpanded = uuids
    },
    addItemExpanded(state: any, uuid: any) {
        state.itemsExpanded.push(uuid)
    },
    addAssetUuid(state: any, uuid: any) {
        if (!state.assets.includes(uuid)) {
            state.assets.push(uuid)
        }
    },
    removeAssetUuid(state: any, uuid: any) {
        state.assets = state.assets.filter((asset: any) => {
            return asset !== uuid
        })
    },
}

const actions = {
    login({commit}: any, user: User) {
        commit('setUser', user)
    },
    async resetResponse({commit, state}: any, payload: any) {
        await commit("resetResponse", payload);
    },

    /**
     * Sync core : charge les données de structure depuis POST /api/sync
     * Reconstruit les entités plates en objets exploitables :
     *  - itemTypes indexés par code, properties complètes (type résolu, choices, conditionGroups)
     *  - insurers indexés par id, avec packs rattachés (packGuarantees incluses)
     *  - packCategories, guarantees, avenantTypes, etc.
     */
    async syncCore({commit, state, rootState}: any, payload: { branchSlug?: string, branchId?: number, scopes?: string[] } = {}) {
        const {api} = require('@/services/Api')
        const branchIds = payload.branchId ? [payload.branchId] : undefined
        const res = await api.post(api.core, 'api/sync', {
            since: state._syncCoreSince || null,
            branches: branchIds,
            scopes: payload.scopes || null,
        })
        if (!res?.data?.state) return

        const s = res.data.state
        const dataTypes = rootState.masterStore?.dataTypes || []
        // Indexer les dataTypes par id pour un lookup rapide
        const dtById: Record<number, any> = {}
        dataTypes.forEach((dt: any) => { if (dt.id) dtById[dt.id] = dt })

        // --- Reconstruire les itemProperties ---
        const allProperties: any = s.itemProperties?.updates || {}
        const allChoices: any = s.itemPropertyChoices?.updates || {}
        const allConditionGroups: any = s.itemPropertyConditionGroups?.updates || {}
        const allConditions: any = s.itemPropertyConditions?.updates || {}

        let unresolvedTypes = 0
        // Résoudre chaque property
        for (const [pid, prop] of Object.entries(allProperties) as any[]) {
            // Résoudre type (int) → objet DataType avec {id, code, format, label}
            if (prop.type && typeof prop.type === 'number') {
                const dt = dtById[prop.type]
                if (dt) {
                    prop.type = {id: dt.id, code: dt.code, format: dt.format, label: dt.label}
                } else {
                    unresolvedTypes++
                    prop.type = {id: prop.type, code: 'TEXT', format: 'TEXT', label: 'Unknown'}
                }
            } else if (!prop.type) {
                prop.type = {id: 0, code: 'TEXT', format: 'TEXT', label: 'Unknown'}
            }
            // Résoudre choices (array d'IDs) → array d'objets
            if (Array.isArray(prop.choices)) {
                prop.choices = prop.choices.map((cid: number) => allChoices[cid]).filter(Boolean)
            }
            // Résoudre conditionGroups (array d'IDs) → array d'objets avec conditions
            if (Array.isArray(prop.conditionGroups)) {
                prop.conditionGroups = prop.conditionGroups.map((cgid: number) => {
                    const cg = allConditionGroups[cgid]
                    if (!cg) return null
                    // Résoudre conditions dans le groupe
                    if (Array.isArray(cg.conditions)) {
                        cg.conditions = cg.conditions.map((condId: number) => {
                            const cond = allConditions[condId]
                            if (!cond) return null
                            // Résoudre testingProperty (ID) → objet avec code
                            if (cond.property && allProperties[cond.property]) {
                                cond.testingProperty = {
                                    id: allProperties[cond.property].id,
                                    code: allProperties[cond.property].code,
                                    name: allProperties[cond.property].name,
                                }
                            }
                            return cond
                        }).filter(Boolean)
                    }
                    return cg
                }).filter(Boolean)
            }
        }
        if (unresolvedTypes > 0) console.warn('[syncCore] unresolved types:', unresolvedTypes, '/', Object.keys(allProperties).length)

        // --- Trouver le branchId courant ---
        let currentBranchId: number | null = payload.branchId || null
        if (!currentBranchId && payload.branchSlug && s.branches?.updates) {
            for (const b of Object.values(s.branches.updates) as any[]) {
                if (b.slug === payload.branchSlug) { currentBranchId = b.id; break }
            }
        }

        // --- Reconstruire les itemTypes indexés par code (filtrés par branche) ---
        const rawItemTypes: any = s.itemTypes?.updates || {}
        const itemTypes: any = {}
        for (const [id, it] of Object.entries(rawItemTypes) as any[]) {
            // Filtrer par branche si connu
            if (currentBranchId && it.branch !== currentBranchId) continue
            // Transformer method string → objet {code}
            if (it.method && typeof it.method === 'string') {
                it.method = {code: it.method}
            }
            const props: any = {}
            let missingProps = 0
            if (Array.isArray(it.properties)) {
                it.properties.forEach((pid: number) => {
                    const prop = allProperties[pid]
                    if (prop) {
                        props[prop.code] = prop
                        if (!prop.type || typeof prop.type !== 'object') {
                            console.warn('[syncCore] property', prop.code, 'on', it.code, 'has bad type:', prop.type)
                        }
                    } else if (pid > 0) {
                        missingProps++
                    }
                })
            }
            if (missingProps > 0) console.warn('[syncCore] itemType', it.code, 'missing', missingProps, 'properties')
            it.properties = props
            itemTypes[it.code] = it
        }

        // --- Reconstruire les insurers avec packs ---
        const rawInsurers: any = s.insurers?.updates || {}
        const rawPacks: any = s.packs?.updates || {}
        const rawPackGuarantees: any = s.packGuarantees?.updates || {}
        const rawGuarantees: any = s.guarantees?.updates || {}
        const rawPackCategories: any = s.packCategories?.updates || {}

        // Résoudre les packGuarantees
        for (const [pgid, pg] of Object.entries(rawPackGuarantees) as any[]) {
            if (pg.guarantee && rawGuarantees[pg.guarantee]) {
                pg.guarantee = rawGuarantees[pg.guarantee]
            }
        }

        // Résoudre les packs avec category, insurer et packGuarantees
        const insurerMap: any = {}
        for (const [iid, ins] of Object.entries(rawInsurers) as any[]) {
            insurerMap[iid] = {...ins, packs: []}
        }
        const packList: any[] = []
        for (const [pid, pack] of Object.entries(rawPacks) as any[]) {
            // Résoudre category
            if (pack.category && rawPackCategories[pack.category]) {
                pack.category = rawPackCategories[pack.category]
            }
            // Résoudre insurer
            if (pack.insurer && insurerMap[pack.insurer]) {
                const ins = insurerMap[pack.insurer]
                pack.insurer = {id: ins.id, slug: ins.slug, label: ins.label, photo: ins.photo}
                ins.packs.push(pack)
            }
            // Résoudre packGuarantees
            if (Array.isArray(pack.packGuarantees)) {
                pack.packGuarantees = pack.packGuarantees
                    .filter((pgid: number) => pgid > 0)
                    .map((pgid: number) => rawPackGuarantees[pgid])
                    .filter(Boolean)
            } else {
                pack.packGuarantees = []
            }
            pack.enable = pack.enable !== false
            packList.push(pack)
        }

        // --- Commit tout dans le store ---
        if (s.itemTypes) commit('setEntity', {type: 'itemTypes', data: itemTypes})
        if (s.insurers) commit('setEntity', {type: 'insurers', data: insurerMap})
        if (s.packs) commit('setEntity', {type: 'packs', data: packList})
        if (s.packCategories) commit('setEntity', {type: 'packCategories', data: Object.values(rawPackCategories)})
        if (s.guarantees) commit('setEntity', {type: 'guarantees', data: rawGuarantees})
        if (s.avenantTypes) commit('setEntity', {type: 'avenantTypes', data: s.avenantTypes.updates || {}})
        if (s.branches) {
            // Trouver la branche courante
            const branches = s.branches.updates || {}
            for (const b of Object.values(branches) as any[]) {
                if (payload.branchSlug && b.slug === payload.branchSlug) {
                    commit('setEntity', {type: 'branch', data: b})
                } else if (payload.branchId && b.id === payload.branchId) {
                    commit('setEntity', {type: 'branch', data: b})
                }
            }
        }
        if (s.premiumDetailModels) commit('setEntity', {type: 'premiumDetailModels', data: Object.values(s.premiumDetailModels.updates || {})})
        if (s.reductionModels) commit('setEntity', {type: 'reductionModels', data: Object.values(s.reductionModels.updates || {})})

        // Sauvegarder le timestamp pour le delta sync
        Vue.set(state, '_syncCoreSince', res.data.at)
    },
}

const getters = {
    getEntities: (state: any) => (type: string) => {
        if (!state.hasOwnProperty(type)) {
            return null
        }
        return state[type];
    },
    getEntitiesByUuid: (state: any) => (type: string, uuids: any) => {
        if (!state.hasOwnProperty(type)) {
            return null
        }
        const collection = state[type];

        if (!collection || typeof collection !== 'object') {
            return null;
        }
        return Object.values(collection).filter((item: any) => uuids.includes(item.uuid)) || null;
    },
    getEntity: (state: any) => (type: string, value: string) => {
        if (!state.hasOwnProperty(type)) {
            return null
        }
        const collection = state[type];
        if (collection && collection.hasOwnProperty(value)) {
            return collection[value]
        }
        return null;
    },
    getEntityProperty: (state: any) => (itemUuid: string, propertyUuid: string) => {
        if (state.items && state.items.hasOwnProperty(itemUuid)) {
            const item = state.items[itemUuid]
            for (let property of Object.values(item.properties) as any[]) {
                if (property.uuid === propertyUuid) {
                    return property
                }
            }
        }
        return null;
    },
    getEntityTypeProperties: (state: any, getters: any, rootState: any, rootGetters: any) => (code: string) => {
        if (state.itemTypes && state.itemTypes.hasOwnProperty(code)) {
            const properties = []
            const itemType = state.itemTypes[code]
            for (let property of Object.values(itemType.properties) as any[]) {
                const dataType = rootState.masterStore.dataTypes.find((datatype: any) => {
                    return datatype.id === property.typeCode
                })
                if (dataType && !dataType.code.match(/OBJECT/)) {
                    properties.push({uuid: property.uuid, name: property.name})
                }
            }
            return properties
        }
        return [];
    },
    getEntityByProperty: (state: any) => (type: string, value: string, property = 'uuid') => {
        const collection = state[type];
        if (!collection || typeof collection !== 'object') {
            return null;
        }
        return Object.values(collection).find((item: any) => item[property] === value) || null;
    },
    avenant: (state: any) => {
        return Object.values(state.items).filter((item: any) => {
            const itemModel = state.itemTypes[item.modelCode]
            return itemModel.model.code === 'avenant'
        })
    },
    requiredItems: (state: any) => {
        return Object.values(state.items).filter((item: any) => {
            const itemModel = state.itemTypes[item.modelCode]
            return itemModel?.required
        })
    },
    requiredItemsAndRoot: (state: any) => {
        return Object.values(state.items).filter((item: any) => {
            const itemModel = state.itemTypes[item.modelCode]
            return itemModel?.required || itemModel?.isRoot
        })
    }
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
}
